import { Switch, Route, Redirect, useLocation } from "wouter";
import { useEffect, lazy, Suspense } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { useAuth } from "@/hooks/useAuth";
import { usePrelaunch } from "@/hooks/use-prelaunch";
import { usePageTracking } from "@/hooks/use-page-tracking";
import { RouteErrorBoundary } from "@/components/route-error-boundary";

// Landing is the LCP route and the entry point for nearly all traffic, so it
// stays in the main bundle — code-splitting it would add a round trip to the
// one paint that matters. Everything else loads on demand.
import Landing from "@/pages/landing";

const NotFound = lazy(() => import("@/pages/not-found"));
const Waitlist = lazy(() => import("@/pages/waitlist"));
const Login = lazy(() => import("@/pages/login"));
const Signup = lazy(() => import("@/pages/signup"));
const Register = lazy(() => import("@/pages/register"));
const ResetPassword = lazy(() => import("@/pages/reset-password"));
const Pricing = lazy(() => import("@/pages/pricing"));
const HowItWorks = lazy(() => import("@/pages/how-it-works"));
const DocsPage = lazy(() => import("@/pages/docs"));
const FAQ = lazy(() => import("@/pages/faq"));
const Privacy = lazy(() => import("@/pages/privacy"));
const Terms = lazy(() => import("@/pages/terms"));

const AdminDashboard = lazy(() => import("@/pages/admin-dashboard"));
const AdminAnalytics = lazy(() => import("@/pages/admin-analytics"));
const AdminAnalyticsPage = lazy(() => import("@/pages/admin-analytics-page"));
const AdminAnalyticsSessions = lazy(() => import("@/pages/admin-analytics-sessions"));
const AdminAnalyticsSessionDetail = lazy(() => import("@/pages/admin-analytics-session-detail"));
const AdminAnalyticsVisitor = lazy(() => import("@/pages/admin-analytics-visitor"));
const AdminApiKeys = lazy(() => import("@/pages/admin-api-keys"));
const AdminUsers = lazy(() => import("@/pages/admin-users"));
const AdminWaitlist = lazy(() => import("@/pages/admin-waitlist"));
const AdminUserDetail = lazy(() => import("@/pages/admin-user-detail"));
const AdminLogs = lazy(() => import("@/pages/admin-logs"));
const AgentTraffic = lazy(() => import("@/pages/agent-traffic"));

const DashboardApiKeys = lazy(() => import("@/pages/dashboard-api-keys"));
const DashboardBilling = lazy(() => import("@/pages/dashboard-billing"));
const DashboardOverview = lazy(() => import("@/pages/dashboard-overview"));
const DashboardPlayground = lazy(() => import("@/pages/dashboard-playground"));
const DashboardUsage = lazy(() => import("@/pages/dashboard-usage"));
const DashboardSettings = lazy(() => import("@/pages/dashboard-settings"));

function RouteSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <RouteSpinner />;
  }

  if (!user) {
    return <Redirect to="/login" />;
  }

  return <Component />;
}

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

// During pre-launch the auth pages themselves are closed: anyone landing on
// /login, /signup or /register is sent to the waitlist instead. Admins sign
// in through the form on /admin, which stays open.
function GatedRoute({ component: Component }: { component: React.ComponentType }) {
  const prelaunch = usePrelaunch();

  if (prelaunch) {
    return <Redirect to="/waitlist" />;
  }

  return <Component />;
}

function Router() {
  usePageTracking();

  return (
    <>
      <ScrollToTop />
      <RouteErrorBoundary>
        <Suspense fallback={<RouteSpinner />}>
          <Switch>
            <Route path="/" component={Landing} />
            <Route path="/waitlist" component={Waitlist} />
            <Route path="/login">{() => <GatedRoute component={Login} />}</Route>
            <Route path="/signup">{() => <GatedRoute component={Signup} />}</Route>
            <Route path="/register">{() => <GatedRoute component={Register} />}</Route>
            <Route path="/reset-password" component={ResetPassword} />
            <Route path="/pricing" component={Pricing} />
            <Route path="/how-it-works" component={HowItWorks} />
            <Route path="/docs" component={DocsPage} />
            <Route path="/faq" component={FAQ} />
            <Route path="/privacy" component={Privacy} />
            <Route path="/terms" component={Terms} />
            <Route path="/admin" component={AdminDashboard} />
            <Route path="/admin/analytics" component={AdminAnalytics} />
            <Route path="/admin/analytics/page" component={AdminAnalyticsPage} />
            <Route path="/admin/analytics/sessions" component={AdminAnalyticsSessions} />
            <Route path="/admin/analytics/sessions/:sessionId" component={AdminAnalyticsSessionDetail} />
            <Route path="/admin/analytics/visitors/:sessionId" component={AdminAnalyticsVisitor} />
            <Route path="/admin/api-keys" component={AdminApiKeys} />
            <Route path="/admin/users" component={AdminUsers} />
            <Route path="/admin/waitlist" component={AdminWaitlist} />
            <Route path="/admin/users/:id" component={AdminUserDetail} />
            <Route path="/admin/business-analytics">{() => <Redirect to="/admin/analytics" />}</Route>
            <Route path="/admin/logs" component={AdminLogs} />
            <Route path="/admin/agent-traffic" component={AgentTraffic} />
            <Route path="/dashboard">{() => <ProtectedRoute component={DashboardOverview} />}</Route>
            <Route path="/dashboard/api-keys">{() => <ProtectedRoute component={DashboardApiKeys} />}</Route>
            <Route path="/dashboard/billing">{() => <ProtectedRoute component={DashboardBilling} />}</Route>
            <Route path="/dashboard/playground">{() => <ProtectedRoute component={DashboardPlayground} />}</Route>
            <Route path="/dashboard/usage">{() => <ProtectedRoute component={DashboardUsage} />}</Route>
            <Route path="/dashboard/settings">{() => <ProtectedRoute component={DashboardSettings} />}</Route>
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </RouteErrorBoundary>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
